// ============================================================================
// AssigneePicker.tsx — выбор ИСПОЛНИТЕЛЯ задачи (поле assignee_id) с
// загруженностью: список пользователей с ролью «Исполнитель» (SSoT — сервер,
// SuggestAssignees), у каждого — бейдж «N активных задач»; свободные выше.
// «Кто свободен/малозанят» — видно сразу, без перегруженных форм.
// Включается для поля «Исполнитель» типа задачи (включаемый механизм).
// ============================================================================

import { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { Label } from '@/components/ui/label';
import { getSDK } from '@/store/sdkStore';
import { filterPeople, type PersonLike } from '@/lib/people';
import { Search, Check, User } from 'lucide-react';

interface AssigneeCandidate extends PersonLike {
  entityId: string;
  name: string;
  skills?: string[];
  activeCount?: number;
}

interface AssigneePickerProps {
  value?: string;
  displayValue?: string;
  /** Тип задачи (таблица) — активные задачи считаются по нему (пусто = все) */
  taskType?: string;
  disabled?: boolean;
  required?: boolean;
  onChange: (entityId: string) => void;
}

export function AssigneePicker({ value, displayValue, taskType, disabled, required, onChange }: AssigneePickerProps) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AssigneeCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const loadedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r: any = await getSDK().task.suggestAssignees({ taskType: taskType || '' } as any);
      setItems(filterPeople(((r?.items || []) as any[]).map((c) => ({
        entityId: String(c.entityId || ''),
        name: String(c.name || c.entityId || ''),
        login: String(c.login || ''),
        kind: String(c.kind || ''),
        isBot: !!c.isBot,
        isSystem: !!c.isSystem,
        createdVia: String(c.createdVia || ''),
        skills: (c.skills || []) as string[],
        activeCount: Number(c.activeCount || 0),
      }))));
    } catch (e: any) {
      setError(e?.message || 'Не удалось загрузить исполнителей');
    } finally {
      setLoading(false);
    }
  }, [taskType]);

  useEffect(() => {
    if (open && !loadedRef.current) {
      void load();
      loadedRef.current = true;
    }
    if (!open) loadedRef.current = false;
  }, [open, load]);

  const filtered = items.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return c.name.toLowerCase().includes(q) || (c.skills || []).some((s) => s.toLowerCase().includes(q));
  });

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <Input
          value={displayValue || String(value || '')}
          readOnly
          placeholder="Не выбран"
          className="h-8 text-sm flex-1"
          disabled={disabled}
        />
        <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={() => setOpen(true)} disabled={disabled}>
          …
        </Button>
        {value ? (
          <Button size="sm" variant="ghost" className="h-8 text-[11px]" onClick={() => onChange('')}>✕</Button>
        ) : null}
      </div>
      {error && <div className="text-[10px] text-destructive">{error}</div>}

      {open && (
        <UniversalWindow open onClose={() => setOpen(false)} title="Выбор исполнителя" width="w-[min(480px,94vw)]" resizable={false}>
          <div className="p-2 space-y-2 max-h-[70vh] flex flex-col">
            <div className="flex items-center gap-1 relative">
              <Search className="h-3.5 w-3.5 absolute left-2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Поиск по имени или навыку…"
                className="h-8 text-[12px] pl-7"
              />
            </div>
            {loading ? (
              <div className="text-[11px] text-muted-foreground p-2">Загрузка исполнителей…</div>
            ) : (
              <div className="flex-1 overflow-y-auto space-y-1">
                {filtered.length === 0 && (
                  <div className="text-[11px] text-muted-foreground p-2">
                    {search ? 'Ничего не найдено' : 'Исполнители не настроены (роль «Исполнитель»)'}
                  </div>
                )}
                {filtered.map((c) => {
                  const active = Number(c.activeCount || 0);
                  const selected = value && String(value) === c.entityId;
                  return (
                    <button
                      key={c.entityId}
                      type="button"
                      className={`w-full text-left px-2 py-1.5 rounded-md border text-xs flex items-center gap-2 transition-colors ${
                        selected ? 'border-primary bg-primary/10' : 'border-transparent hover:bg-accent'
                      }`}
                      onClick={() => {
                        onChange(c.entityId);
                        setOpen(false);
                      }}
                    >
                      <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="flex-1 min-w-0">
                        <span className="block truncate">{c.name}</span>
                        {(c.skills || []).length > 0 || active > 0 ? (
                          <span className="block truncate text-[9px] text-muted-foreground">
                            {(c.skills || []).join(', ')}
                            {active > 0 ? `${(c.skills || []).length > 0 ? ' · ' : ''}активных задач: ${active}` : ''}
                          </span>
                        ) : null}
                      </span>
                      <span
                        className={`shrink-0 h-5 min-w-5 px-1.5 flex items-center justify-center rounded-full text-[10px] font-medium ${
                          active === 0
                            ? 'bg-emerald-500/15 text-emerald-600'
                            : active <= 2
                              ? 'bg-amber-500/15 text-amber-600'
                              : 'bg-destructive/15 text-destructive'
                        }`}
                        title={`Активных задач: ${active}`}
                      >
                        {active}
                      </span>
                      {selected && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </UniversalWindow>
      )}
    </div>
  );
}
